import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Table from '../../../../UI/Table'
import MiniHeader from '../../../../UI/MiniHeader'
import { useTranslation } from 'react-i18next'

import styles from './style.module.scss'
import Search from '../../../../UI/Search'
import Btn from '../../../../UI/Btn'
import { TApi, TRoles, TRoutes } from '../../../../shared/const'
import http from '../../../../shared/http'
import Pagination from '../../../../UI/Pagination'
import BtnTooltip from '../../../../UI/BtnTooltip'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { companyTypeField, schoolStatusField, schoolTypeField, stateField } from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import { toast } from 'react-toastify'
import { blobTypes, downloadFile, stateFullLabel, stateLabel } from '../../../../shared/helpers'
import { useIsReadOnly } from '../../../../shared/alumni'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'
import clsx from 'clsx'
import Icon from '../../../../UI/Icon'
import { connect } from 'react-redux'

function Schools(props) {
	const { t } = useTranslation()
	const history = useHistory()
	const [schools, setSchools] = useState([])
	const readOnly = useIsReadOnly()
	const [countSchools, setCountSchools] = useState(0)
	const [page, setPage] = useState(0)
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const [loading, setLoading] = useState(false)
	const limit = 10

	// Bundesland-Admins sehen ohnehin nur ihr eigenes Bundesland (das setzt das
	// Backend), ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			schoolTypeField(t, props.schoolTypes),
			schoolStatusField(t),
			companyTypeField(t)
		],
		[t, props.role, props.schoolTypes]
	)

	const filters = useFilters({ storageKey: 'schools', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchSchools = () => {
		const isCurrent = latest()

		http.get(TApi.ADMIN_GET_SCHOOLS, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				filter: filterQuery,
				search: debouncedSearch
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			setSchools(data.rows)
			setCountSchools(data.count)
		})
	}

	/*
	 * Die Spalten werden nur bei einem Sprachwechsel neu gebaut. Riefen ihre
	 * Handlungen `fetchSchools` direkt, laedt die Liste danach mit der Abfrage
	 * vom ersten Aufbau neu — also ohne Suchtext, Filter und Seitenzahl.
	 */
	const fetchRef = useRef(fetchSchools)
	fetchRef.current = fetchSchools

	const confirmSchool = id => {
		http.put(TApi.ADMIN_SCHOOL_VERIFY(id), { verified: true }).then(() => {
			toast.success(t('The school has been approved'))
			fetchRef.current()
		})
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchSchools()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, debouncedSearch, filterQuery])

	const columns = useMemo(
		() => [
			{
				Header: 'Name',
				accessor: 'name',
				minWidth: 250,
				Cell: ({ row }) => {
					return (
						<div className={styles.name}>
							{/* Farbe und Schnitt der Namensspalte kommen aus UI/Table. */}
							<span>{row.original.name}</span>
							{/* Alumni (#213): weder Zusammenführen noch Freigeben,
							    und die Detailseite ist ein Bearbeitungsformular. */}
							{!readOnly && (
								<BtnTooltip
									items={[
										{
											label: t('Merge school'),
											onClick: () =>
												eventEmitter.emit(types.openMergeSchoolModal, row.original, () =>
													fetchRef.current()
												)
										},
										{
											label: t('details'),
											onClick: () => history.push(`${TRoutes.UPDATE_SCHOOL(row.original.id)}`)
										},
										{
											label: t('Approve school'),
											onClick: () => confirmSchool(row.original.id),
											disabled: row.values.verified
										}
										/*{
							label: t('Remove'),
							onClick: () => deleteSchool(row.original.id),
							disabled: row.original.Members?.length
						}*/
									]}
								/>
							)}
						</div>
					)
				}
			},
			{
				Header: t('state'),
				accessor: 'state',
				cellToolTip: ({ row }) => stateFullLabel(row.original.state),
				Cell: ({ row }) => stateLabel(row.original.state),
				width: 70
			},
			{
				Header: t('Type'),
				accessor: 'type',
				Cell: ({ row }) => {
					return <span>{row.original.type}</span>
				},
				width: 200
			},
			{
				Header: t('Number of companies'),
				accessor: 'schoolCompanies',
				Cell: ({ row }) => {
					return <span>{row.original.schoolCompanies}</span>
				},
				disableSortBy: true,
				width: 100
			},
			{
				Header: t('Number of Students'),
				accessor: 'schoolStudents',
				Cell: ({ row }) => {
					return <span>{row.original.schoolStudents}</span>
				},
				disableSortBy: true,
				width: 100
			},
			{
				Header: 'Status',
				accessor: 'verified',
				Cell: ({ row }) => {
					return (
						<div className={styles.status}>
							<p style={row.original.verified ? { color: 'green' } : { color: '#ef8400' }}>
								{row.original.verified ? t('Verified') : t('Not Verified')}
							</p>
						</div>
					)
				},
				width: 60
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, readOnly]
	)
	const downoloadList = fileType => {
		setLoading(true)
		http.get(TApi.ADMIN_DOWNOLOAD_SCHOOLS_LIST, {
			params: {
				type: fileType,
				leng: localStorage.getItem('i18nextLng')
			},
			responseType: 'arraybuffer'
		})
			.then(res => {
				var blob = new Blob([res.data], {
					type: blobTypes[fileType]
				})
				downloadFile('Schools', blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}
	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('schools')}>
				<div className={styles.miniHeaderContent}>
					<Search
						onChange={e => handleSearchChange(e.target.value)}
						value={search}
						onSubmit={e => e.preventDefault()}
						placeholder={t('Find a school')}
					/>
					<BtnFilter
						icon="arrow"
						onClick={() => setShowFilter(true)}
						count={filters.count}
					>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('schools')} countAll={countSchools} onChange={setPage} />

				{!readOnly && (
					<Btn
						// loading={isLoading}
						type="button"
						className={styles.addBtn}
						icon="plus"
						iconColor="white"
						onClick={() => history.push(TRoutes.CREATE_SCHOOL)}
					>
						{t('Create School')}
					</Btn>
				)}
				{!readOnly && (
					<div className={styles.downloadContent}>
						{t('Download list as')}
						<div>
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('XLS')
									}}
								>
									XLS
								</Btn>
							)}
							{loading && (
								<Icon
									className={clsx(styles.icon, styles.rootLoading)}
									size={{ w: 16, h: 16 }}
									name="loading"
									color={'#22404D'}
								/>
							)}
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('PDF')
									}}
								>
									PDF
								</Btn>
							)}
						</div>
					</div>
				)}
			</div>
			<div className="container">
				<Table
					items={schools}
					handleSort={handleSort}
					columns={columns}
					limit={limit}
					countAll={countSchools}
					onChange={setPage}
					page={page}
					paginationLabel={t('schools')}
					onRowClick={readOnly ? undefined : school => history.push(TRoutes.UPDATE_SCHOOL(school.id))}
					rowTitle={readOnly ? undefined : t('openRecord')}
				/>
			</div>
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	role: state.user.role,
	schoolTypes: state.options.schoolTypes
})

export default connect(mapStateToProps)(Schools)
